import { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { authService, buildGlobalUser, clearPersistedTokenFallback } from '../services/authService';
import { resolveSignInUser } from '../services/resolveSignInUser';
import { reportProfileLoadFailure, reportRoleRejected } from '../services/profileReport';
import { earlySession } from '../earlySession';
import { User as FirebaseUser } from 'firebase/auth';
import { setUser, useUser, setRooftop, setCustomerConfig, setCustomerConfigLoaded, setOrganizationConfig, clearPermissions, getNonReactiveState } from '@store';
import { toast } from 'react-toastify';
import { checkForPermittedRole, type ProfileLoadFailure } from '../services/authService';

/** A Firebase user is signed in but the portal could not load their HopDrive account. The app
 *  renders the "couldn't load your account" screen in place of the routes until a retry
 *  succeeds or they sign out. */
export interface AccountLoadFailure {
  failure?: ProfileLoadFailure;
  retrying: boolean;
}

export const useAuth = () => {
  const user = useUser();

  const [loading, setLoading] = useState(true);
  const [accountFailure, setAccountFailure] = useState<AccountLoadFailure | null>(null);
  // The sign-in step for the current Firebase user, so the failure screen's Retry can run it again.
  const signInRef = useRef<((phase: 'sign_in' | 'account_retry') => Promise<void>) | null>(null);
  // The uid the persisted-session boot loaded, kept for the whole session (the local
  // `earlyUid` below is cleared after the first token event).
  const earlyUidRef = useRef<string | undefined>(undefined);
  // The Firebase uid whose sign-in is running. A forced token refresh inside that sign-in
  // fires onIdTokenChanged while no user is stored yet, which would otherwise start a second,
  // overlapping sign-in for the same account.
  const signInInFlightRef = useRef<string | null>(null);

  useEffect(() => {
    // If the persisted-session boot produced a user, put them in the store now, before the
    // Firebase SDK has finished its own account lookup. The SDK's verdict still
    // lands below: a matching uid is a token refresh, a null user is a sign-out, and a
    // different uid is a different account signing in.
    let earlyUid: string | undefined;
    let cancelled = false;
    earlySession().then(early => {
      if (cancelled || !early) return;
      if (getNonReactiveState().user) return; // Firebase got there first.
      earlyUid = early.uid;
      earlyUidRef.current = early.uid;
      setUser(early.user);
      setLoading(false);
      Sentry.setUser({
        id: early.user.profile?.id ? String(early.user.profile.id) : undefined,
        email: early.user.profile?.email || undefined,
        username: early.user.profile?.display_name || undefined,
      });
    });

    /**
     * Load the HopDrive account for a signed-in Firebase user and enter the portal. A profile
     * that does not load after the retries is reported and leaves the portal behind the
     * account screen. The Firebase session stays, so Retry needs no new sign-in, and nothing
     * clears local state.
     */
    const signIn = async (firebaseUser: FirebaseUser, phase: 'sign_in' | 'account_retry') => {
      if (signInInFlightRef.current === firebaseUser.uid) return;
      signInInFlightRef.current = firebaseUser.uid;
      try {
        await resolveAndEnter(firebaseUser, phase);
      } finally {
        if (signInInFlightRef.current === firebaseUser.uid) signInInFlightRef.current = null;
      }
    };

    const resolveAndEnter = async (firebaseUser: FirebaseUser, phase: 'sign_in' | 'account_retry') => {
      const resolution = await resolveSignInUser({
        build: buildGlobalUser,
        refreshIdToken: authService.refreshIdToken,
        isPermittedRole: checkForPermittedRole,
      });
      // Signed out, or another account signed in, while this ran: its answer no longer applies.
      if (authService.currentUid() !== firebaseUser.uid) return;

      if (resolution.status === 'role_rejected') {
        reportRoleRejected({ user: resolution.user, firebaseUid: firebaseUser.uid, diagnostics: resolution.diagnostics });
        toast.error(`The user entered does not have access to this application. If you think this is an error, please contact a Hopdrive admin.`);
        setAccountFailure(null);
        authService.logout();
        return;
      }

      if (resolution.status === 'profile_missing') {
        // The early boot may have stored this same account with its profile while these
        // attempts ran. Someone already using the portal keeps it.
        const stored = getNonReactiveState().user;
        if (stored?.profile && earlyUidRef.current === firebaseUser.uid) {
          setAccountFailure(null);
          setLoading(false);
          return;
        }
        reportProfileLoadFailure({
          phase,
          user: resolution.user,
          failure: resolution.failure,
          error: resolution.error,
          firebaseUid: firebaseUser.uid,
          attempts: resolution.attempts,
          action: 'blocked',
          stage: resolution.stage,
          diagnostics: resolution.diagnostics,
          history: resolution.history,
        });
        setAccountFailure({ failure: resolution.failure, retrying: false });
        setLoading(false);
        return;
      }

      const globalUser = resolution.user;
      console.log(`(onIdTokenChanged) Auto-logging in ${firebaseUser?.displayName} using active session...`, {
        firebaseUser,
        globalUser,
        attempts: resolution.attempts,
      });
      setAccountFailure(null);
      setUser(globalUser);
      setLoading(false);

      // Attach user identity to all Sentry events for the session.
      Sentry.setUser({
        id: globalUser?.profile?.id ? String(globalUser.profile.id) : undefined,
        email: globalUser?.profile?.email || undefined,
        username: globalUser?.profile?.display_name || undefined,
      });

      // Track user activity on login
      if (globalUser?.profile?.id) {
        authService.updateUserActivity(globalUser.profile.id);
      }
    };

    const unsubscribe = authService.onIdTokenChanged(async (firebaseUser: FirebaseUser | null) => {
      // Let the persisted-session boot finish first. If it loaded this account, its user is
      // in the store by now, this event is a token refresh, and startup sends one profile
      // request instead of two.
      await earlySession();
      // Get current user from store to avoid stale closure
      let currentUser = getNonReactiveState().user;
      if (currentUser && firebaseUser && earlyUid && firebaseUser.uid !== earlyUid) {
        // A different account than the persisted one: drop the early user and sign in fresh.
        setUser();
        currentUser = undefined;
      }
      earlyUid = undefined;

      const isLoggingIn = !!(!currentUser && firebaseUser);
      const isLoggingOut = !!(currentUser && !firebaseUser);
      const isLoggedOut = !!(!currentUser && !firebaseUser);
      const isTokenRefresh = !!(currentUser && firebaseUser);

      if (isLoggingIn && firebaseUser) {
        // The token refresh a running sign-in forced for this account: that sign-in is
        // already reading the new token, so this event has nothing to add.
        if (signInInFlightRef.current === firebaseUser.uid) return;
        signInRef.current = phase => signIn(firebaseUser, phase);
        await signIn(firebaseUser, 'sign_in');
      }

      if (isTokenRefresh) {
        // Track user activity on token refresh
        if (currentUser?.profile?.id) {
          authService.updateUserActivity(currentUser.profile.id);
        }
      }

      if (isLoggingOut) {
        console.log(`(onIdTokenChanged) Auto-logging out ${currentUser?.profile?.display_name}...`, {
          isLoggingIn,
          isLoggingOut,
          isLoggedOut,
          currentUser,
          firebaseUser,
        });

        // Clear localStorage and store to prevent data leakage between users
        localStorage?.clear();
        clearPersistedTokenFallback();

        // Clear all store state
        setUser();
        setRooftop();
        setCustomerConfig();
        setCustomerConfigLoaded(false);
        setOrganizationConfig();
        clearPermissions();
        setLoading(false);

        // Clear Sentry user identity so the next session starts clean.
        Sentry.setUser(null);
        signInRef.current = null;
        signInInFlightRef.current = null;
        setAccountFailure(null);
      }

      if (isLoggedOut) {
        console.log('(onIdTokenChanged) is currently logged out. Nothing to do.', {
          isLoggingIn,
          isLoggingOut,
          isLoggedOut,
          currentUser,
          firebaseUser,
        });

        // Clear localStorage and store to prevent data leakage between users
        localStorage?.clear();
        clearPersistedTokenFallback();

        // Clear all store state
        setUser();
        setRooftop();
        setCustomerConfig();
        setCustomerConfigLoaded(false);
        setOrganizationConfig();
        clearPermissions();
        signInRef.current = null;
        signInInFlightRef.current = null;
        setAccountFailure(null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  /** Run the account load again for the signed-in Firebase user (the account screen's Retry). */
  const retryAccount = useCallback(async () => {
    const run = signInRef.current;
    if (!run) return;
    setAccountFailure(current => (current ? { ...current, retrying: true } : current));
    await run('account_retry');
  }, []);

  /** Sign out from the account screen. The normal sign-out path clears local state. */
  const signOutFromAccountFailure = useCallback(() => {
    authService.logout();
  }, []);

  return { user, loading, accountFailure, retryAccount, signOutFromAccountFailure };
};
